import { http } from './http'
import { ResponseType } from 'axios'

interface Hash<U> {
  [key: string]: U
}

let loading: Hash<boolean> = {}
let cancelled: Hash<boolean> = {}
let pending: Hash<any[]> = {}
let cached: Hash<boolean> = {}

const setCached = (key: string, data?: any) => {
  loading[key] = false
  cached[key] = data
  // Resolve all pending requests
  if (pending[key]) {
    pending[key].forEach(pendingRequest => {
      // resolve/reject are the optional request handlers passed to the cachedRequest() call.
      // resolve2/reject2 are the secondary handlers created to resolve or reject this pending
      // request after the original request is complete.
      const { resolve, resolve2, reject2 } = pendingRequest
      // TODO: Consider refactoring to remove `.data` here and make caching more generic
      handleResolve({ key, resolveFn: resolve, data: { data: cached[key] } })
        .then(resolve2)
        .catch(reject2)
    })
    pending[key] = []
  }
}

const failCached = (key: string, error?: any) => {
  loading[key] = false
  delete cached[key]
  if (pending[key]) {
    pending[key].forEach(pendingRequest => {
      // resolve/reject are the optional request handlers passed to the cachedRequest() call.
      // resolve2/reject2 are the secondary handlers created to resolve or reject this pending
      // request after the original request is complete.
      const { reject, reject2 } = pendingRequest
      handleReject({ key, rejectFn: reject, error })
        .then(reject2)
        .catch(reject2)
    })
    pending[key] = []
  }
}

export const clearCache = () => {
  const keys = Object.keys(cached)
  const loadingKeys = Object.keys(loading)
  ;[...keys, ...loadingKeys].forEach(key => {
    cancelled[key] = true
    failCached(key, 'Cache cleared')
  })
}

// clearCache() should be used within the application, but a full reset is useful for unit testing
export const resetCache = () => {
  clearCache()
  loading = {}
  cancelled = {}
  pending = {}
  cached = {}
}

const handleResolve = (params: {
  key: string
  resolveFn?: any
  data: any
  updateCache?: boolean
}) => {
  const { key, resolveFn, data, updateCache } = params
  // if the dispatch operation specified a resolve function, call it and see if it returns
  // a promise. If so, we'll let it resolve first before caching and completing the request.
  let promise = null
  if (resolveFn) promise = resolveFn(data)
  if (promise) {
    return promise
      .then((resp: any) => {
        if (updateCache) setCached(key, resp)
        return Promise.resolve(resp)
      })
      .catch((error: any) => {
        if (updateCache) failCached(key, error)
        return Promise.reject(error)
      })
  } else {
    // TODO: Consider refactoring to remove `.data` here and make caching more generic
    if (updateCache) setCached(key, data.data)
    return Promise.resolve(data.data)
  }
}

const handleReject = (params: {
  key: string
  rejectFn?: any
  error: any
  updateCache?: boolean
}) => {
  const { key, rejectFn, error, updateCache } = params
  // if the dispatch operation specified a reject function, call it and see if it returns
  // a promise. If so, we'll let it resolve first before caching and completing the request.
  let promise = null
  if (rejectFn) promise = rejectFn(error)
  if (promise) {
    return promise
      .then((msg: any) => {
        if (updateCache) failCached(key, msg)
        return Promise.reject(msg)
      })
      .catch((err: any) => {
        if (updateCache) failCached(key, err)
        return Promise.reject(err)
      })
  } else {
    if (updateCache) failCached(key, error)
    return Promise.reject(error)
  }
}

type HTTP_VERB = 'get' | 'post' | 'put' | 'delete'

interface CachedRequestOptions {
  url: string
  method?: HTTP_VERB
  params?: object
  data?: object
  resolve?: Function
  reject?: Function
  isLoading?: Function
  refresh?: boolean
  responseType?: ResponseType
}

export const cachedRequest = (options: CachedRequestOptions) => {
  const { url, refresh, isLoading, resolve, reject, params, data, ...rest } = options
  const paramsKey = Object.values(params || {}).length > 0 ? '?' + JSON.stringify(params) : ''
  const dataKey = Object.values(data || {}).length > 0 ? '|' + JSON.stringify(data) : ''
  const key = url + paramsKey + dataKey
  const method = options.method || 'get'

  // Return immediately if the request has already been cached
  if (!refresh && typeof cached[key] !== 'undefined') {
    // TODO: Consider refactoring to remove `.data` here and make caching more generic
    return handleResolve({ key, resolveFn: resolve, data: { data: cached[key] } })
  }

  // Call the isLoading callback, so that appropriate loading indicators can be displayed
  if (isLoading) isLoading()

  // If the data is already being loaded, return a promise that will be resolved later
  if (loading[key]) {
    return new Promise((resolve2, reject2) => {
      pending[key] = [...(pending[key] || []), { resolve, reject, resolve2, reject2 }]
    })
  }

  // If we made it this far, we're responsible for loading the data
  loading[key] = true
  cancelled[key] = false

  // Set up a function to make the API call
  const makeRequest = () => {
    return http({ url, method, params, data, ...rest })
      .then(response => {
        if (cancelled[key]) {
          // Throw away the response if the request was cancelled. Neither handleResolve or
          // handleReject can be used here, because of how the store might be mutated incorrectly.
          return
        }
        return handleResolve({ key, resolveFn: resolve, data: response, updateCache: true })
      })
      .catch(error => {
        if (cancelled[key]) {
          // Throw away the error if the request was cancelled. Neither handleResolve or
          // handleReject can be used here, because of how the store might be mutated incorrectly.
          return
        }
        return handleReject({ key, rejectFn: reject, error, updateCache: true })
      })
  }

  // Make the request and return a promise
  return makeRequest()

  // FOR DEV TESTING, THIS ADDS DELAYS AND CAUSES SOME API REQUESTS TO FAIL, WHICH IS USEFUL FOR
  // TESTING. COMMENT THE THE RETURN STATEMENT ABOVE AND UNCOMMENT THIS:
  // return new Promise((res, rej) => {
  //   setTimeout(() => {
  //     // For specific API failures, it can be helpful to swap the random condition for
  //     // something like `if (key === '/api/account?accountId=default') {...}`
  //     if (Math.random() > 0.9) {
  //       const error = new Error('Failed: ' + key)
  //       if (reject) reject(error)
  //       failCached(key)
  //       rej(error)
  //     } else {
  //       makeRequest()
  //         .then(res)
  //         .catch(rej)
  //     }
  //   }, 1000)
  // })
}
